import { useCallback } from "react"
import { graphql } from "react-relay"
import { t } from "i18next"
import { useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import {
  useUploadDoc2UReportMutation$data,
  useUploadDoc2UReportMutation as useUploadDoc2UReportMutationType,
} from "@/modules/patients/detail/progress-note/doc2u/services/__generated__/useUploadDoc2UReportMutation.graphql"

const mutation = graphql`
  mutation useUploadDoc2UReportMutation($input: UploadDoc2USessionReportInput!) {
    uploadDoc2USessionReport(input: $input) {
      uploadAppointmentFilesOutput {
        ... on SuccessUploadAppointmentFilesOutput {
          attachments {
            id
            url
            thumbnailUrl
            uploadDate
            attachmentType
            uploadSource
            uploadStatus
          }
          isSuccess
        }
        ... on ErrorUploadAppointmentFilesOutput {
          isSuccess
          error {
            fileName
            message
          }
        }
      }
    }
  }
`
type MutationParams = {
  appointmentId: string
  file: File
  handleError?: (error: Error | string | undefined) => void
  handleCompleted?: (
    attachments: NonNullable<
      useUploadDoc2UReportMutation$data["uploadDoc2USessionReport"]["uploadAppointmentFilesOutput"]
    >["attachments"],
  ) => void
}

const useUploadDoc2UReportMutation = () => {
  const [commit, isFlight] = useMutationWithErrorDialog<useUploadDoc2UReportMutationType>(mutation, {
    confirmProps: {
      title: t("patient.uploadFiles.unableToUpload"),
    },
  })

  const commitHandler = useCallback(
    async ({ appointmentId, file, handleCompleted, handleError }: MutationParams) => {
      commit({
        variables: {
          input: {
            appointmentId,
            file,
          },
        },
        onCompleted({ uploadDoc2USessionReport }) {
          const errorMessage = uploadDoc2USessionReport.uploadAppointmentFilesOutput?.error?.message
          const isSuccess = uploadDoc2USessionReport.uploadAppointmentFilesOutput?.isSuccess
          const attachments = uploadDoc2USessionReport.uploadAppointmentFilesOutput?.attachments

          if (isSuccess) {
            handleCompleted?.(attachments)
          } else {
            handleError?.(errorMessage)
          }
        },
        onError(error) {
          handleError?.(error.message)
        },
        updater: (store, data) => {
          const appointmentRecordProxy = store.get(appointmentId)
          if (!appointmentRecordProxy) return
          const appointmentAttachments = appointmentRecordProxy.getLinkedRecords("appointmentAttachments") || []
          const attachments = data?.uploadDoc2USessionReport.uploadAppointmentFilesOutput?.attachments || []
          attachments.forEach((attachment) => {
            const attachmentId = attachment.id
            const existingAttachment = appointmentAttachments.find((att) => att.getDataID() === attachmentId)
            if (existingAttachment) return null
            const newAttachment = store.get(attachmentId)
            if (newAttachment) {
              appointmentAttachments.unshift(newAttachment)
            }
          })
          appointmentRecordProxy.setLinkedRecords(appointmentAttachments, "appointmentAttachments")
        },
      })
    },
    [commit],
  )

  return [commitHandler, isFlight] as const
}

export { useUploadDoc2UReportMutation }
